import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { rgpdClient } from '../lib/rgpd';
import type { PrivacyPolicy } from '../lib/rgpd';
import { logger } from '../lib/logger';

export const usePrivacyPolicy = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fetch privacy policy data
  const { data: policy, isLoading: policyLoading } = useQuery({
    queryKey: ['privacy-policy'],
    queryFn: () => rgpdClient.getPrivacyPolicy(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
  });

  // Fetch RGPD config data
  const { data: config, isLoading: configLoading } = useQuery({
    queryKey: ['rgpd-config'],
    queryFn: () => rgpdClient.getConfig(),
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
  });

  const openModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const getLastUpdate = useCallback(() => {
    if (!policy || policy.length === 0) return null;

    try {
      // Trouver la date de mise à jour la plus récente
      const latestUpdate = policy.reduce((latest: Date | null, item: PrivacyPolicy) => {
        const itemDate = new Date(item.updated_at);
        if (!latest || itemDate > latest) {
          return itemDate;
        }
        return latest;
      }, null);

      return latestUpdate?.toISOString() || null;
    } catch (error) {
      logger.error('Error getting last update date', {
        category: 'Privacy',
        data: { error }
      });
      return null;
    }
  }, [policy]);

  return {
    policy,
    config,
    isModalOpen,
    openModal,
    closeModal,
    isLoading: policyLoading || configLoading,
    getLastUpdate,
  };
};